import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, can } from '../lib/api';
import type { Page, Site } from '../lib/types';
import { Badge, Button, Empty, Icon, LoadError, Skeleton } from '../lib/ui';
import { NewSiteWizard } from '../features/setup/NewSiteWizard';
import { navigate } from '../app';
import { Hilfe } from '../features/help/Help';
import { fmtDate, t } from '../lib/i18n';

export function Sites() {
  const [creating, setCreating] = useState(false);

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: () => api<Page<Site>>('/v1/sites'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center text-2xl font-semibold">
          {t('Websites')}
          <Hilfe to="website-anlegen" />
        </h1>
        {can('site:create') && !creating && (
          <Button onClick={() => setCreating(true)}><Icon name="plus" /> {t('Website hinzufügen')}</Button>
        )}
      </div>

      {creating && (
        <NewSiteWizard
          onCancel={() => setCreating(false)}
          onDone={(site) => { setCreating(false); navigate(`/sites/${site.id}`); }}
        />
      )}

      {sites.isPending && <Skeleton rows={3} />}
      {sites.isError && <LoadError what={t('Die Websites')} onRetry={() => void sites.refetch()} />}

      {sites.isSuccess && sites.data.data.length === 0 && (
        <Empty
          title={t('Noch keine Website')}
          hint={can('site:create')
            ? t('Eine Website ist eine Domain mit einem eigenen Banner. Nach dem Anlegen bekommen Sie ein Snippet für den <head>.')
            : t('Ihre Rolle darf keine Website anlegen. Bitten Sie jemanden mit der Rolle „admin“ oder „owner“ darum.')}
          action={can('site:create') && !creating
            ? <Button onClick={() => setCreating(true)}>{t('Erste Website anlegen')}</Button>
            : undefined}
        />
      )}

      <div className="grid gap-3">
        {sites.data?.data.map((s) => (
          <button
            key={s.id}
            onClick={() => navigate(`/sites/${s.id}`)}
            className="group flex items-center justify-between gap-4 rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3 text-left transition-colors hover:border-[var(--color-brand)]/60 hover:bg-[var(--color-sunken)]"
          >
            <div className="min-w-0">
              <div className="text-[15px] font-medium tracking-[-0.01em]">{s.name}</div>
              {/* The site key was printed in full — 30 characters of base62 that nobody
                  reads and that pushed the domain, the thing people navigate by, into
                  second place on a line of noise. It lives on the site's own page. */}
              <div className="mt-0.5 text-xs text-[var(--color-muted)]">{s.primary_domain}</div>
            </div>
            <div className="flex shrink-0 items-center gap-3 text-xs">
              {s.config_version ? (
                <>
                  <span className="hidden text-[var(--color-muted)] sm:inline">
                    {t('seit {date}', { date: fmtDate(s.published_at!) })}
                  </span>
                  <Badge tone="live">{t('live')}</Badge>
                </>
              ) : (
                // Never published means the CDN returns 404 and the SDK stays on its
                // fail-closed seed rules — worth saying plainly rather than showing a dash.
                <Badge tone="note">{t('nie veröffentlicht')}</Badge>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
